import Select from "react-select";
import React, { useMemo, useState, useEffect } from "react";
import countryList from "react-select-country-list";
import { toast } from "react-toastify";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const BusinessAccount = ({
  formData,
  errors,
  setFormData,
  handleChange: handleSignupChange,
  handleSubmitForm,
}) => {
  const [location, setLocation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState("");
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameMessage, setUsernameMessage] = useState("");
  const [passwordCriteria, setPasswordCriteria] = useState({
    minLength: false,
    alphanumeric: false,
    specialChar: false,
  });

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: "56px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      !passwordCriteria.minLength ||
      !passwordCriteria.alphanumeric ||
      !passwordCriteria.specialChar
    ) {
      toast.error("Password does not meet criteria");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setPasswordMatchError("Passwords do not match");
      return;
    }

    handleSubmitForm(e);
  };

  const handleCountryChange = (selectedOption) => {
    setFormData((prevState) => ({
      ...prevState,
      countryCode: selectedOption.dialCode, // Update the country code
    }));
  };
  const getLocation = async () => {
    try {
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();
      if (!data?.city || !data?.country_name) {
        throw new Error("Location data unavailable");
      }
      const address = `${data.city}, ${data.country_name}`;
      setLocation(address);
      setFormData((p) => ({ ...p, location: address }));
    } catch {
      toast.error(
        "Couldn't detect your location automatically. Please enter it manually below."
      );
    }
  };

  /* ===================== CONSTANTS ===================== */
  const countryOptions = [
    {
      code: "US",
      name: "United States",
      dialCode: "+1",
      flagUrl: "https://flagcdn.com/w40/us.png",
    },
    {
      code: "GB",
      name: "United Kingdom",
      dialCode: "+44",
      flagUrl: "https://flagcdn.com/w40/gb.png",
    },
    {
      code: "CA",
      name: "Canada",
      dialCode: "+1",
      flagUrl: "https://flagcdn.com/w40/ca.png",
    },
    {
      code: "AU",
      name: "Australia",
      dialCode: "+61",
      flagUrl: "https://flagcdn.com/w40/au.png",
    },
    {
      code: "DE",
      name: "Germany",
      dialCode: "+49",
      flagUrl: "https://flagcdn.com/w40/de.png",
    },
    {
      code: "FR",
      name: "France",
      dialCode: "+33",
      flagUrl: "https://flagcdn.com/w40/fr.png",
    },
    {
      code: "JP",
      name: "Japan",
      dialCode: "+81",
      flagUrl: "https://flagcdn.com/w40/jp.png",
    },
    {
      code: "CN",
      name: "China",
      dialCode: "+86",
      flagUrl: "https://flagcdn.com/w40/cn.png",
    },
    {
      code: "IN",
      name: "India",
      dialCode: "+91",
      flagUrl: "https://flagcdn.com/w40/in.png",
    },
    {
      code: "BR",
      name: "Brazil",
      dialCode: "+55",
      flagUrl: "https://flagcdn.com/w40/br.png",
    },
  ];
  const countryOptionsList = useMemo(() => countryList().getData(), []);

  const checkPasswordMatch = (confirmPassword) => {
    setPasswordMatchError(
      confirmPassword !== formData.password ? "Passwords do not match" : ""
    );
  };

  const checkUsernameAvailability = async (username) => {
    if (!username) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/auth/verify-username`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username }),
        }
      );
      const data = await res.json();

      if (data?.result?.available) {
        setUsernameAvailable(true);
        setUsernameMessage("Username is available");
      } else {
        setUsernameAvailable(false);
        setUsernameMessage("Username already taken");
      }
    } catch {
      setUsernameMessage("Error checking username");
    }
  };
  /* ===================== HELPERS ===================== */
  const validatePassword = (password) => {
    setPasswordCriteria({
      minLength: password.length >= 8,
      alphanumeric: /[A-Za-z]/.test(password) && /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  // Re-sync the criteria checklist whenever the password value changes for any reason
  // (typing, or being restored from storage after returning from the T&C page)
  useEffect(() => {
    validatePassword(formData.password || "");
  }, [formData.password]);

  /* ===================== HANDLERS ===================== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    // Signup's handler keeps names to letters and sets/clears these fields' errors
    if (name === "firstname" || name === "lastname" || name === "businessName") {
      handleSignupChange(e);
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "username") checkUsernameAvailability(value);
    if (name === "password") validatePassword(value);
    if (name === "confirmPassword") checkPasswordMatch(value);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 signup-scroll">
      <div>
        <label className="text-sm font-medium">Business / Brand Name</label>
        <input
          type="text"
          name="businessName"
          placeholder="Enter your Brand / Business Name"
          value={formData.businessName}
          onChange={handleChange}
          className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
        />
        {errors.businessName && <p className="text-red-500 text-sm">{errors.businessName}</p>}
      </div>

      {[
        { name: "firstname", label: "Owner First Name", placeholder: "Enter First Name" },
        { name: "lastname", label: "Owner Last Name", placeholder: "Enter Last Name" },
      ].map((field) => (
        <div key={field.name}>
          <label className="text-sm font-medium">{field.label}</label>
          <input
            type="text"
            name={field.name}
            placeholder={field.placeholder}
            value={formData[field.name]}
            maxLength={50}
            onChange={handleChange}
            className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
          />
          {errors[field.name] && <p className="text-red-500 text-sm">{errors[field.name]}</p>}
        </div>
      ))}

      <div>
        <label className="text-sm font-medium">User Name</label>
        <input
          type="text"
          name="username"
          placeholder="Enter your User Name"
          value={formData.username}
          onChange={handleChange}
          className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
        />
      </div>

      <div>
        <label className="text-sm font-medium">Date of Incorporation</label>
        <input
          type="date"
          name="incorporationDate"
          value={formData.incorporationDate}
          onChange={handleChange}
          className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
        />
      </div>

      <div>
        <label className="text-sm font-medium">Business Email Address</label>
        <input
          type="email"
          name="email"
          placeholder="Enter your Email"
          value={formData.email}
          onChange={handleChange}
          className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
        />
      </div>
      <input
        type={showPassword ? "text" : "password"}
        name="password"
        placeholder="Password"
        value={formData.password}
        onChange={handleChange}
        className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
      />

      {/* Password criteria checklist */}
      <div className="password-checkmar text-sm text-gray-600 flex flex-wrap gap-x-6">
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={passwordCriteria.minLength}
            readOnly
            className="w-4 h-4 accent-green-500"
          />
          <span>Min 8 characters</span>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={passwordCriteria.alphanumeric}
            readOnly
            className="w-4 h-4 accent-green-500"
          />
          <span>Alphanumeric</span>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            checked={passwordCriteria.specialChar}
            readOnly
            className="w-4 h-4 accent-green-500"
          />
          <span>Special character</span>
        </div>
      </div>

      <input
        type={showConfirmPassword ? "text" : "password"}
        name="confirmPassword"
        placeholder="Confirm Password"
        value={formData.confirmPassword}
        onChange={handleChange}
        className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
      />

      {passwordMatchError && (
        <p className="text-red-500 text-sm">{passwordMatchError}</p>
      )}
      <input
        name="location"
        value={formData.location || location}
        onChange={handleChange}
        onClick={() => {
          if (!formData.location) getLocation();
        }}
        placeholder="Location (auto-detected, or type your own)"
        className="w-full h-[60px] px-3 border border-gray-400 rounded-xl"
      />

      <Select
        options={countryOptionsList}
        styles={customStyles}
        menuPortalTarget={document.body}
        placeholder="Country"
        value={countryOptionsList.find((option) => option.label === formData.country) || null}
        onChange={(opt) =>
          setFormData((p) => ({ ...p, country: opt?.label || "" }))
        }
      />
      {/* Phone Number Input */}
      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="mobile">
          Mobile Number
        </label>
        <div className="flex space-x-2 items-center">
          <Select
            options={countryOptions}
            getOptionLabel={(e) => (
              <div className="flex items-center">
                <img src={e.flagUrl} alt="" className="w-6 h-4" />
                <span className="ml-2 hidden">{e.dialCode}</span>
              </div>
            )}
            getOptionValue={(e) => e.dialCode}
            value={countryOptions.find(
              (c) => c.dialCode === formData.countryCode
            )}
            onChange={handleCountryChange}
            styles={customStyles}
            menuPortalTarget={document.body} // Ensures popup behavior
            menuPosition="absolute"
          />

          <input
            id="mobile"
            placeholder="Mobile Number"
            type="tel"
            name="mobile"
            value={formData.mobile}
            maxLength={15}
            onChange={handleChange}
            className="w-full max-w-[300px] h-[56px] px-3 py-2 border border-gray-400 rounded-[12px] focus:outline-none focus:ring focus:ring-blue-200"
          />
        </div>
      </div>

      {errors.mobile && <p className="text-red-500 text-sm">{errors.mobile}</p>}
      <button
        type="submit"
        className="w-full h-[56px] bg-[#161F28] text-white rounded-xl hover:bg-[#1B2537]"
      >
        Create Account
      </button>
    </form>
  );
};

export default BusinessAccount;
